import { createReducer } from 'redux-act';
import {
  changeCollectionStateSuccess,
  clearCollection,
  fetchCollectionFailed,
  fetchCollectionSuccess
} from '../../actions/collections';
import { IGalleryTag, IShopSalesAutomation } from '../../types';
import { CommentsMode } from '../../types/comments';
import { COLLECTION_SHOP_AUTOMATION_DEFAULTS, DEFAULT_SETTINGS_TAB } from '../../constants';
import { DesignColors } from '../../constants/colorPresets';
import moment from 'moment';

interface ICollectionButtonStyle {
  backgroundColor: string;
  color: string;
}

interface ICollectionFont {
  fontFamily: string;
  name: string;
}

interface ICollectionSharing {
  show: boolean;
}

interface ICollectionStyles {
  backgroundColor: string;
}

export interface ICustomDomain {
  domain: string;
  path: string;
  url: string;
}

export interface IEndCustomer {
  user: string;
  type: string;
  email: string;
  language: number;
  name: string;
  firstName?: string;
  lastName?: string;
  marketingEmailsEnabled?: boolean;
  unsubscribedFromMarketingEmails?: boolean;
  endCustomerNumber?: string;
  telephoneNumber?: string;
  sevDeskId?: string;
  _country?: string;
  street?: string;
  streetNumber?: string;
  zipCode?: string;
  city?: string;
  password?: string;
  createdAt: Date;
  updatedAt: Date;
  hiddenImages?: string[];
  id?: string;
  [key: string]: string | number | Date | string[] | boolean;
}

export interface ICollectionsReducerState {
  readonly buttonStyle: ICollectionButtonStyle;
  readonly caching: number;
  readonly comment: boolean;
  // How comments were switched off - drives what the gallery does with existing
  // comments. Only ever written by the DisableCommentsModal (newCommentsDesign).
  commentsMode?: CommentsMode;
  readonly created_at: string;
  customDomain: ICustomDomain;
  readonly deleted: number;
  readonly description: string;
  readonly download: boolean;
  downloadPin: string;
  readonly downloadPinActive: boolean;
  eventDate: string;
  readonly font: ICollectionFont;
  readonly images: any[];
  readonly imgsFolder: string;
  readonly isLR: boolean;
  readonly isStudio: boolean;
  readonly language: number;
  readonly like: boolean;
  readonly mjMessageId: any[];
  name: string;
  readonly responsiveImages: boolean;
  readonly sharing: ICollectionSharing;
  readonly showAppInstall: boolean;
  showImageCaptionInGallery: boolean;
  readonly singleImageDownloadDisabled: boolean;
  readonly statistic: string;
  readonly styles: ICollectionStyles;
  readonly templateName: string;
  readonly signedWilCardUrl: string;
  title: string;
  type: string;
  readonly _galleryPreset: string;
  readonly updated_at: string;
  readonly userBrand: string;
  readonly __v: number;
  readonly _id: string;
  readonly _user: any;
  password: string | null;
  globalDownloadState: boolean;
  globalWatermarkID: string;
  messages?: string[];
  galleryStyle: string;
  activeTab: string;
  shopActive: boolean;
  showOnlyPurchasedPrints: boolean;
  _productPricingList: string;
  salesAutomationConfig: IShopSalesAutomation;
  shopCtaPlacement: string;
  design: {
    disableDecorations: boolean;
    showTrademark?: boolean;
    loadFonts: string[];
    imageSpacing: string;
    reactionSet?: string;
    iconSet?: string;
    variables: {
      [key: string]: string;
    };
    /** id of the pinned palette, empty when custom colours are used */
    newColorPreset?: string;
    newUseCustomColors?: boolean;
    /** The redesign's six colours, kept apart from `variables` so both can run at once. */
    newVariables: DesignColors;
  };
  digitalShop: {
    enabled: boolean;
    manualOrderProcessing: boolean;
    alreadyPurchasedTier: string | null;
  };
  receiver?: string;
  security: {
    wrongPasswordTries: number | null;
    passwordTriesLockedUntil: Date | null;
  };
  tags?: IGalleryTag[];
  expirationDate?: Date | null;
  mainCustomers?: IEndCustomer[];
  faceRecognition: {
    lockedForChange?: boolean;
    enabled: boolean;
    updatedAt: string;
  };
}

const initialState: ICollectionsReducerState = {
  buttonStyle: {
    backgroundColor: '',
    color: ''
  },
  caching: 1,
  comment: false,
  commentsMode: 'enabled',
  created_at: '',
  customDomain: {
    domain: '',
    path: '',
    url: ''
  },
  deleted: 0,
  description: '',
  download: true,
  downloadPin: '',
  downloadPinActive: false,
  eventDate: '',
  font: {
    fontFamily: '',
    name: ''
  },
  signedWilCardUrl: '',
  images: [],
  imgsFolder: '',
  _galleryPreset: '',
  isLR: false,
  isStudio: true,
  language: 1,
  like: true,
  mjMessageId: [],
  name: '',
  responsiveImages: true,
  sharing: {
    show: true
  },
  showAppInstall: true,
  showImageCaptionInGallery: false,
  singleImageDownloadDisabled: false,
  showOnlyPurchasedPrints: false,
  statistic: '',
  styles: {
    backgroundColor: ''
  },
  templateName: '',
  title: '',
  type: '',
  updated_at: '',
  userBrand: '',
  password: '',
  __v: 0,
  _id: '',
  _user: {},
  globalDownloadState: false,
  globalWatermarkID: '',
  galleryStyle: 'justified',
  activeTab: localStorage.getItem('collection:settings:lastTab') || DEFAULT_SETTINGS_TAB,
  shopActive: false,
  _productPricingList: '',
  salesAutomationConfig: COLLECTION_SHOP_AUTOMATION_DEFAULTS,
  shopCtaPlacement: 'after-last',
  design: {
    disableDecorations: false,
    showTrademark: false,
    loadFonts: [],
    imageSpacing: '',
    reactionSet: '',
    iconSet: '',
    variables: {
      font: '',
      primaryColor: '',
      secondaryColor: ''
    },
    newColorPreset: '',
    newUseCustomColors: false,
    newVariables: {
      primaryColor: '',
      secondaryColor: '',
      uiBackgroundColor: '',
      uiTextColor: '',
      accentColor: '',
      coverTextColor: ''
    }
  },
  digitalShop: {
    enabled: false,
    manualOrderProcessing: false,
    alreadyPurchasedTier: null
  },
  security: {
    wrongPasswordTries: null,
    passwordTriesLockedUntil: null
  },
  tags: [],
  expirationDate: null,
  messages: [],
  faceRecognition: {
    enabled: false,
    updatedAt: moment().toLocaleString()
  }
};
export const collectionNew = createReducer<ICollectionsReducerState>({}, initialState);
collectionNew.on(
  fetchCollectionSuccess,
  (state: ICollectionsReducerState, payload: ICollectionsReducerState) => ({
    ...state,
    ...payload,
    design: payload.design
      ? {
          ...payload.design,
          // @ts-ignore
          loadFonts: payload.design.loadFonts.map((font) => font.name),
          // Absent on every collection saved before the new colour fields existed, and on
          // any response from a backend that has not shipped them yet.
          newVariables: payload.design.newVariables || {}
        }
      : {
          loadFonts: [],
          variables: {},
          newVariables: {},
          disableDecorations: false,
          imageSpacing: ''
        }
  })
);
collectionNew.on(
  changeCollectionStateSuccess,
  (state: ICollectionsReducerState, payload: Partial<ICollectionsReducerState>) => ({
    ...state,
    ...payload
  })
);
collectionNew.on(fetchCollectionFailed, () => initialState);
collectionNew.on(clearCollection, () => initialState);
